import React, { useContext, useEffect, useState } from "react";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { ParagraphText, UBAppModal, UBInput } from "../../../../Libraries";
import { LanguageContext } from "../../../../context/LanguageContext";
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableCell,
} from "@mui/material";
import TableRowItem from "./tableRow";
import styles from "./styles.module.css";
import withObservables from "@nozbe/with-observables";
import { combineLatest, map, of, switchMap } from "rxjs";
import { ObserveDraftSaleOrderByBusiness } from "../../../../database/helpers/sale_order";
import { CloseIcon, SearchIcon } from "../../../../assets/icons/icon";

const OrderDraftListModal = ({
  isOpen,
  handleClose = () => {},
  navigate,
  onResume = () => {},
  onDelete = () => {},
  orders,
}) => {
  const [I18n] = useContext(LanguageContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderData, setOrderData] = useState([]);
  const [sort, setSort] = useState({ column: "date", direction: "" }); // Combined state for column and direction
  const [originalData, setOriginalData] = useState([]);
  const [orderFilterData, setOrderFilterData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setOrderData(orders);
        setOriginalData(orders);
        setOrderFilterData(orders);
      } catch (error) {
      }
    };

    fetchData();
  }, [orders, isOpen]);

  useEffect(() => {
    if (isOpen) {
      triggerLogEvent(analytics, "pos_draft_modal_open", {
        draft_count: orders?.length ?? 0,
      });
      setSearchTerm("");
    }
  }, [isOpen]);

  const onFilterData = (data, searchText) => {
    return data.filter(
      (order) =>
        `#${order?._raw?.id?.toLowerCase()}`.includes(
          searchText.toLowerCase(),
        ) ||
        order?.customer?.name
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        String(order?.totalAmount)
          .toLowerCase()
          .includes(searchText.toLowerCase()),
    );
  };

  const handleSort = (column) => {
    let newDirection;
    if (sort.column === column) {
      newDirection =
        sort.direction === "asc"
          ? "desc"
          : sort.direction === "desc"
            ? ""
            : "asc";
    } else {
      newDirection = "asc";
    }
    setSort({ column, direction: newDirection });

    if (!newDirection) {
      const filterData = onFilterData(originalData, searchTerm);
      setOrderFilterData(filterData);
      setOrderData([...originalData]);
      return;
    }

    const sortedData = [...orderData].sort((a, b) => {
      let valA, valB;
      if (column === "date") {
        valA = new Date(a._raw.created_at);
        valB = new Date(b._raw.created_at);
      } else if (column === "customer") {
        valA = a?.customer?.name || "";
        valB = b?.customer?.name || "";
      } else if (column === "saleID") {
        valA = a?._raw?.id || "";
        valB = b?._raw?.id || "";
      } else {
        return 0;
      }

      if (column === "date") {
        return newDirection === "asc" ? valA - valB : valB - valA;
      } else {
        return newDirection === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
    });
    const filterData = onFilterData(sortedData, searchTerm);
    setOrderFilterData(filterData);
    setOrderData(sortedData);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    const filterData = onFilterData(orderData, value);
    setOrderFilterData(filterData);
  };

  const onAddAmountProperty = (amount, index) => {
    let tempOrderData = [...originalData];
    tempOrderData[index].totalAmount = amount;
    setOrderData(tempOrderData);
    setOriginalData(tempOrderData);
  };

  return (
    <UBAppModal
      id="salepos_draft"
      open={isOpen}
      header={{
        title: "draft_sale",
        handleClose: () => {
          handleClose();
        },
      }}
      footer={[
        {
          data: {
            title: I18n.close,
            onClick: handleClose,
            id: "draft_close",
            className: styles.closeBtn,
          },
        },
      ]}
      className={"order-draft-list-modal"}
    >
      {orderData.length > 0 ? (
        <Box className={`h-100 ${styles.bodyContainer}`}>
          <UBInput
            id="draft-sale-search"
            inputId="order_draft_search_input"
            placeholder={I18n.search_by_customer_name_or_sale_id}
            value={searchTerm}
            onChange={(_, value) => handleSearch(value)}
            style={{ margin: 0 }}
            maxLength={70}
            prefix={<SearchIcon />}
            suffix={searchTerm ? <CloseIcon className="cursorPointer" /> : null}
            handleSuffix={() => {
              handleSearch("");
            }}
            baseClassname="stockSearchInput"
          ></UBInput>

          <TableContainer
            component={Paper}
            className="generic-scrollbar"
            sx={{ overflowY: "auto" }}
          >
            <Table stickyHeader>
              <TableHead className={styles.tableHeader} sx={{ height: "44px" }}>
                <TableRow>
                  <TableCell className={styles.tableHeaderCell} id={"customer"}>
                    {I18n.customer}
                  </TableCell>
                  <TableCell className={styles.tableHeaderCell} id={"date"}>
                    {I18n.date}
                  </TableCell>
                  <TableCell className={styles.tableHeaderCell} id={"order_no"}>
                    {I18n.order_no}
                  </TableCell>
                  <TableCell
                    id="amount"
                    className={`${styles.tableHeaderCell} ${styles.textRight}`}
                  >
                    {I18n.amount}
                  </TableCell>
                  <TableCell
                    className={`${styles.tableHeaderCell} ${styles.textCenter}`}
                    id="action"
                  >
                    {I18n.actions}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {orderFilterData.length > 0 ? (
                  orderFilterData.map((item, index) => (
                    <TableRowItem
                      key={item._raw.id}
                      order={item}
                      onDelete={(order) => {
                        triggerLogEvent(analytics, "pos_draft_deleted", {
                          order_id: order?._raw?.id,
                        });
                        onDelete(order);
                      }}
                      onResume={(order) => {
                        triggerLogEvent(analytics, "pos_draft_selected", {
                          order_id: order?._raw?.id,
                        });
                        onResume(order);
                      }}
                      addAmountProperty={(amount) =>
                        onAddAmountProperty(amount, index)
                      }
                    />
                  ))
                ) : (
                  <td colspan="5" style={{ textAlign: "center", padding: 10 }}>
                    <ParagraphText className={"foundationInk300"}>
                      {I18n.no_result}
                    </ParagraphText>
                  </td>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <Box className={`h-100 ${styles.nonIdealContainer}`}>
          <ParagraphText className={styles.nonIdealTitle}>
            {I18n.no_draft_sale}
          </ParagraphText>
          <ParagraphText className={styles.nonIdealDesc}>
            {I18n.no_draft_sale_desc}
          </ParagraphText>
        </Box>
      )}
    </UBAppModal>
  );
};

const enhance = withObservables(["orders"], ({ orders, business }) => {
  return {
    orders: ObserveDraftSaleOrderByBusiness(business?.id ?? "").pipe(
      switchMap((orders) => {
        if (!orders.length) return of([]);

        // Map over all orders and observe their nested fields
        return combineLatest(
          orders.map((order) =>
            combineLatest([
              order.customer.observe(), // Observe customer (object)
              order.sale_order_line.observe(), // Observe sale_order_line (array)
            ]).pipe(
              map(([customer, sale_order_line]) => ({
                ...order,
                customer,
                sale_order_line,
              })),
            ),
          ),
        );
      }),
    ),
  };
});
export default enhance(OrderDraftListModal);
